import { PostalAddress } from '../../common/model/postal-address';
import { LocalDate } from '../../core/model/local-date';
import { ModelBase, PARENT_NAVIGATIONS } from '../../core/model/model-base';
import { Money } from '../../core/model/money';
import { FaultNotification } from '../../fault-notification/model/faultNotification';
import { InvoiceDeliveryMethod } from '../../invoice/model/const/enum';
import {
  ColdWaterPaymentKind,
  CreditorResponsibility,
  DirectDebitStatus,
  DirectDebitStatusToString,
  HeatingPaymentKind,
  HotWaterPaymentKind,
  HouseholdElectricityPaymentKind,
  InvoicePeriod,
  LeaseAgreementCategory,
  LeaseAgreementStatus,
  LeaseAgreementStatusToString,
  LeaseAgreementSubCategoryKind,
  OrganizationOfTenantsKind,
  ParkingElectricityPaymentKind,
  RentPaymentKind,
  SubleaseStatus,
} from './const/enums';
import { Contact } from './contact';
import { ContractSecurity } from './contract-security';
import { LeaseAgreementForm } from './lease-agreement-form';
import { LeaseAgreementRentalObject } from './lease-agreement-rental-object';
import { LeaseRow } from './lease-row';
import { NoticePeriod } from './notice-period';
import { BuildingPropertyTaxAssessment } from './building-property-tax-assesment';
import { Representative } from './representative';
import { Tenant } from './tenant';
import { TaxTypeCode } from './tax-type-code';

export class Contract extends ModelBase {
  static readonly [PARENT_NAVIGATIONS] = [Contact]; // <--- highlighting when navigating back to Contact list.

  constructor(category: LeaseAgreementCategory) {
    super();

    this.agreementDate = LocalDate.fromDate(new Date());
    this.category = category;
    this.invoiceAddress = new PostalAddress();
    this.invoicePeriod = InvoicePeriod.Month;
    if (this.category === LeaseAgreementCategory.Dwelling || this.category === LeaseAgreementCategory.Premises)
      this.rentPayment = RentPaymentKind.SpecifiedAdditions;
    else this.rentPayment = RentPaymentKind.TotalRent;
    this.heatingPayment = HeatingPaymentKind.IncludedInTheRent;
    this.coldWaterPayment = ColdWaterPaymentKind.IncludedInTheRent;
    this.hotWaterPayment = HotWaterPaymentKind.IncludedInTheRent;
    this.householdElectricityPayment = HouseholdElectricityPaymentKind.SeparateSubscription;
    this.parkingElectricityPayment = ParkingElectricityPaymentKind.IncludedInTheRent;
    this.parkingChargingElectricityPayment = ParkingElectricityPaymentKind.IncludedInTheRent;
    this.leaseAgreementForms = [];
    this.leaseAgreementRows = [];
    this.faultNotifications = [];
    this.otherLeaseAgreements = [];
    this.securities = [];
    this.noticePeriod = new NoticePeriod();
    this.rentalObjects = [];
    this.status = LeaseAgreementStatus.Draft;
    this.yearlyDueDateMonth = 12;
    this.dueDateMonthOffset = -1;
    this.yearlyDueDateDay = 31;
    this.representatives = [];
  }

  private _agreementDate: LocalDate;
  private _fromDate: LocalDate;
  private _faultNotifications: FaultNotification[] = [];
  private _leaseRows: LeaseRow[] = [];
  private _noticePeriod: NoticePeriod;
  private _toDate: LocalDate;
  private _terminationDate: LocalDate;
  private _lastBillingDate: LocalDate;
  private _desiredMoveOutDate: LocalDate;
  private _giveUpSecurityOfTenureFrom: LocalDate;
  private _giveUpSecurityOfTenureTo: LocalDate;
  private _validContractPeriodFromLandlord: LocalDate;
  private _validContractPeriodToLandlord: LocalDate;
  private _latestTerminationDateLandlord: LocalDate;
  private _currentDuePeriodFrom: LocalDate;
  private _currentDuePeriodTo: LocalDate;
  private _validSubleaseFrom: LocalDate;
  private _directDebitTerminationDate: LocalDate;

  otherLeaseAgreements: Contract[];

  private _securities: ContractSecurity[] = [];

  get hasOtherLeaseAgreements(): boolean {
    return this.otherLeaseAgreements && this.otherLeaseAgreements.length > 0;
  }

  set agreementDate(value: LocalDate) {
    this._agreementDate = LocalDate.fromDate(value);
  }
  get agreementDate(): LocalDate {
    return this._agreementDate;
  }
  buildingName: string;
  readonly category: LeaseAgreementCategory;
  subCategory: LeaseAgreementSubCategoryKind;
  subCategoryCaption: string;
  company: string;
  companyName: string;
  companyNumberAndName: string;
  companyId: string;
  currencyCode: string;
  cultureName: string;
  propertyId: string;
  propertyNumberAndName: string;
  faultNotificationRecipientId: string;
  faultNotificationExecutorGroupId: string;
  faultNotificationExecutorWebUserId: string;
  creditor: string;
  creditorResponsibility: CreditorResponsibility;
  creditorResponsibilityDescription: string;
  depositAmount: Money;
  depositComment: string;
  labeling: string;
  currentPeriodText: string;
  contactLeaseAgreementConnectionTypes: string;
  directDebitStatus: DirectDebitStatus;
  directDebitStatusDetailedCaption: string;
  isValidToday: boolean;
  subleaseStatus: SubleaseStatus;
  earlyTerminationIsUsed: boolean;
  propertyTax: BuildingPropertyTaxAssessment;
  propertyTaxAssessmentUnitType: Map<number, TaxTypeCode>;

  get directDebitStatusCaption(): string {
    return DirectDebitStatusToString(this.directDebitStatus);
  }

  get directDebitTerminationDate(): LocalDate {
    return this._directDebitTerminationDate;
  }
  set directDebitTerminationDate(value: LocalDate) {
    this._directDebitTerminationDate = LocalDate.fromDate(value);
  }
  showResetDirectDebitRequestDate: boolean;
  resetDirectDebitRequestDate: boolean;
  get fromDate(): LocalDate {
    return this._fromDate;
  }
  set fromDate(value: LocalDate) {
    this._fromDate = LocalDate.fromDate(value);
  }
  get lastBillingDate(): LocalDate {
    return this._lastBillingDate;
  }
  set lastBillingDate(value: LocalDate) {
    this._lastBillingDate = LocalDate.fromDate(value);
  }
  get desiredMoveOutDate(): LocalDate {
    return this._desiredMoveOutDate;
  }
  get terminationDate(): LocalDate {
    return this._terminationDate;
  }
  set terminationDate(value: LocalDate) {
    this._terminationDate = LocalDate.fromDate(value);
  }
  set desiredMoveOutDate(value: LocalDate) {
    this._desiredMoveOutDate = LocalDate.fromDate(value);
  }
  get giveUpSecurityOfTenureFrom(): LocalDate {
    return this._giveUpSecurityOfTenureFrom;
  }
  set giveUpSecurityOfTenureFrom(value: LocalDate) {
    this._giveUpSecurityOfTenureFrom = LocalDate.fromDate(value);
  }
  get giveUpSecurityOfTenureTo(): LocalDate {
    return this._giveUpSecurityOfTenureTo;
  }
  set giveUpSecurityOfTenureTo(value: LocalDate) {
    this._giveUpSecurityOfTenureTo = LocalDate.fromDate(value);
  }
  terminatedByText: string;
  invoiceAddress: PostalAddress;
  invoiceDeliveryMethod: InvoiceDeliveryMethod;
  invoiceDeliveryMethodText: string;
  invoiceAddressString: string;
  emailInvoicing: string;
  invoicePeriod: number;
  yearlyDueDateMonth: number;
  periodFromStartMonth: number;
  yearlyDueDateDay: number;
  dueDateMonthOffset: number;
  invoicePayment: number;
  rentPayment: number;
  heatingPayment: number;
  coldWaterPayment: number;
  hotWaterPayment: number;
  householdElectricityPayment: number;
  get faultNotifications(): FaultNotification[] {
    return this._faultNotifications;
  }
  set faultNotifications(items: FaultNotification[]) {
    if (items instanceof Array) {
      items.forEach(item => {
        if (item instanceof FaultNotification) this._faultNotifications.push(item);
        else this._faultNotifications.push(Object.assign(new FaultNotification(), item));
      });
    }
  }
  parkingElectricityPayment: number;
  parkingChargingElectricityPayment: number;
  currentPeriodRentAmount: Money;
  nextPeriodRentAmount: Money;
  nextPeriodRentAmountInclVat: Money;
  leaseAgreementNumber: string;
  popularName: string;
  invoicePeriodText: string;
  blockingWarning: string;
  yearRentPeriodAsText: string;
  leaseAgreementForms: LeaseAgreementForm[];
  get securities(): ContractSecurity[] {
    return this._securities;
  }
  set securities(items: ContractSecurity[]) {
    if (items instanceof Array) {
      items.forEach(item => {
        if (item instanceof ContractSecurity) this._securities.push(Object.assign(new ContractSecurity(), item));
        else this._securities.push(Object.assign(new ContractSecurity(), item));
      });
    }
  }
  get leaseAgreementRows(): LeaseRow[] {
    return this._leaseRows;
  }
  set leaseAgreementRows(items: LeaseRow[]) {
    if (items instanceof Array) {
      items.forEach(item => {
        if (item instanceof LeaseRow) this._leaseRows.push(item);
        else this._leaseRows.push(Object.assign(new LeaseRow(), item));
      });
    }
  }
  get noticePeriod(): NoticePeriod {
    return this._noticePeriod;
  }
  set noticePeriod(value: NoticePeriod) {
    if (value) {
      this._noticePeriod = value;
    } else {
      this._noticePeriod = value;
    }
  }
  originalContractualRent: Money;
  payer: string;
  propertyName: string;
  rentalObjects: LeaseAgreementRentalObject[];
  rentalObject: string;
  rentalId: string;
  rentalObjectId: string;
  rentalObjectTypeName: string;
  rentalObjectRentalId: string;
  rentalObjectSurface: number;
  rentalObjectSumSurface: number;
  rentalObjectParkingNumber: string;
  leaseAgreementArea: number;
  selected: boolean = false;
  specialTerms: string;
  rentPaymentTerms: string;
  note: string;
  status: LeaseAgreementStatus;
  usage: string;
  fastighetsagarnaDokumentActive: boolean;
  eSignatureActive: boolean;
  hasIndexRent: string;
  HasCreditorContact: string;
  individualInvoiceAddress: boolean = false;
  useContractSpecificInvoicingEmail: boolean = false;
  economicalPlanNumber: string;

  negotiatingClause: boolean;
  organizationOfTenants: OrganizationOfTenantsKind;
  otherOrganizationOfTenants: string;

  get statusCaption(): string {
    return LeaseAgreementStatusToString(this.status);
  }
  tenantOne: string | Tenant;
  tenantTwo: string | Tenant;

  emailReceivers: string;
  tenantOneContactIdentification: string;
  tenantTwoContactIdentification: string;
  tenantOneName: string;
  tenantTwoName: string;
  tenantOneNumber: number;
  tenantTwoNumber: number;
  tenantOnePhone: string;
  tenantTwoPhone: string;
  tenantOneEmail: string;
  tenantTwoEmail: string;

  subleaseInformation: string;
  get validSubleaseFrom(): LocalDate {
    return this._validSubleaseFrom;
  }
  set validSubleaseFrom(value: LocalDate) {
    this._validSubleaseFrom = LocalDate.fromDate(value);
  }

  get hasEndDate(): boolean {
    return this.status === LeaseAgreementStatus.Terminated || this.status === LeaseAgreementStatus.Expired;
  }
  // noticePeriod is nullable (see its setter), so both accessors have to tolerate it
  // being absent instead of throwing while the model is being serialized.
  get toDate(): LocalDate {
    return this._toDate ? this._toDate : this.noticePeriod?.to;
  }
  set toDate(value: LocalDate) {
    this._toDate = LocalDate.fromDate(value);
    if (this.noticePeriod) this.noticePeriod.to = this._toDate;
  }

  get validContractPeriodFromLandlord(): LocalDate {
    return this._validContractPeriodFromLandlord;
  }
  set validContractPeriodFromLandlord(value: LocalDate) {
    this._validContractPeriodFromLandlord = LocalDate.fromDate(value);
  }

  get validContractPeriodToLandlord(): LocalDate {
    return this._validContractPeriodToLandlord;
  }
  set validContractPeriodToLandlord(value: LocalDate) {
    this._validContractPeriodToLandlord = LocalDate.fromDate(value);
  }

  get latestTerminationDateLandlord(): LocalDate {
    return this._latestTerminationDateLandlord;
  }
  set latestTerminationDateLandlord(value: LocalDate) {
    this._latestTerminationDateLandlord = LocalDate.fromDate(value);
  }
  get currentDuePeriodFrom(): LocalDate {
    return this._currentDuePeriodFrom;
  }
  set currentDuePeriodFrom(value: LocalDate) {
    this._currentDuePeriodFrom = LocalDate.fromDate(value);
  }
  get currentDuePeriodTo(): LocalDate {
    return this._currentDuePeriodTo;
  }
  set currentDuePeriodTo(value: LocalDate) {
    this._currentDuePeriodTo = LocalDate.fromDate(value);
  }

  representatives: Representative[];

  nameplate: string;

  toJSON() {
    let json = Object.assign({}, this);
    json.leaseAgreementRows = this.leaseAgreementRows;
    json.noticePeriod = this.noticePeriod;
    json.fromDate = this.fromDate;
    json.agreementDate = this.agreementDate;
    json.giveUpSecurityOfTenureFrom = this.giveUpSecurityOfTenureFrom;
    json.giveUpSecurityOfTenureTo = this.giveUpSecurityOfTenureTo;
    json.toDate = this.toDate;
    json.lastBillingDate = this.lastBillingDate;
    json.currentDuePeriodFrom = this.currentDuePeriodFrom;
    json.currentDuePeriodTo = this.currentDuePeriodTo;
    json.company = this.companyId;
    json.securities = this.securities;
    // prepareSave() copies the raw "rentalObject" form control value onto the model via
    // Object.assign, which in the create flow is a full VacantRentalObject instead of the
    // display string the API expects. Serialize it as a string (the API only reads the
    // rentalObjects connection list, this field is a display-only caption).
    const rentalObject = this.rentalObject as any;
    if (rentalObject && typeof rentalObject !== 'string') {
      json.rentalObject = rentalObject.toString !== Object.prototype.toString ? rentalObject.toString() : null;
    }
    //delete json['company'];
    return json;
  }

  toString() {
    return `${this.leaseAgreementNumber}`;
  }
}
